import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTransferUser } from '../auth/useTransferUser';

const TEMPLATE_URL = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1/templates/metadata.csv`;
const SAMPLE_URL = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1/templates/metadata-sample.csv`;
const ONBOARDED_KEY = 'dfx-onboarded';

export function HomePage() {
  const { user, isLoading, error } = useTransferUser();

  if (isLoading) return <div>Loading…</div>;
  if (error) {
    return (
      <div className="rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-6">
        <h1 className="text-xl font-semibold">Account not provisioned</h1>
        <p className="mt-2 text-[color:var(--color-text-secondary)]">
          You're signed in to Auth0, but your account isn't linked to the transfer portal yet.
          If you have an invitation email, click the link inside. Otherwise contact your DFX
          administrator.
        </p>
      </div>
    );
  }
  if (!user) return null;

  if (user.role === 'dfx_super_admin') return <SuperAdminHome email={user.email} />;
  return <UploaderHome email={user.email} role={user.role} />;
}

function SuperAdminHome({ email }: { email: string }) {
  return (
    <div>
      <h1 className="text-2xl font-semibold">Welcome, {email}</h1>
      <p className="mt-2 text-[color:var(--color-text-secondary)]">
        Role: <span className="font-mono">dfx_super_admin</span>
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Link
          to="/orgs"
          className="rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-4 transition hover:border-[color:var(--color-crimson)]"
        >
          <div className="text-sm font-medium">Organizations</div>
          <p className="mt-1 text-sm text-[color:var(--color-text-secondary)]">
            Create orgs and view their licensors.
          </p>
        </Link>
        <Link
          to="/invitations"
          className="rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-4 transition hover:border-[color:var(--color-crimson)]"
        >
          <div className="text-sm font-medium">Invitations</div>
          <p className="mt-1 text-sm text-[color:var(--color-text-secondary)]">
            Invite org admins and uploaders.
          </p>
        </Link>
      </div>
    </div>
  );
}

function UploaderHome({ email, role }: { email: string; role: 'org_admin' | 'org_uploader' }) {
  // First-time visitors see the wizard; returning visitors land in
  // "skip" mode (a one-click path to the upload page).
  const [mode, setMode] = useState<'wizard' | 'skip'>(() => {
    try {
      return window.localStorage.getItem(ONBOARDED_KEY) === 'true' ? 'skip' : 'wizard';
    } catch {
      return 'wizard';
    }
  });

  function setModeAndPersist(next: 'wizard' | 'skip') {
    setMode(next);
    try {
      window.localStorage.setItem(ONBOARDED_KEY, next === 'skip' ? 'true' : 'false');
    } catch {
      // ignore
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[color:var(--color-text-muted)]">
            Welcome
          </div>
          <h1 className="mt-1 text-2xl font-semibold">Get set up to deliver content</h1>
          <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">
            Signed in as <span className="font-mono">{email}</span> ·{' '}
            <span className="font-mono">{role}</span>
          </p>
        </div>
        <ModeToggle mode={mode} onChange={setModeAndPersist} />
      </div>

      {mode === 'wizard' ? (
        <Wizard onFinish={() => setModeAndPersist('skip')} />
      ) : (
        <SkipPanel />
      )}
    </div>
  );
}

function ModeToggle({
  mode,
  onChange,
}: {
  mode: 'wizard' | 'skip';
  onChange: (mode: 'wizard' | 'skip') => void;
}) {
  return (
    <div className="inline-flex rounded border border-[color:var(--color-border)] text-xs">
      <button
        type="button"
        onClick={() => onChange('wizard')}
        className={`px-3 py-1.5 transition ${
          mode === 'wizard'
            ? 'bg-[color:var(--color-bg-card)] text-[color:var(--color-text-primary)]'
            : 'text-[color:var(--color-text-secondary)]'
        }`}
      >
        Show wizard
      </button>
      <button
        type="button"
        onClick={() => onChange('skip')}
        className={`px-3 py-1.5 transition ${
          mode === 'skip'
            ? 'bg-[color:var(--color-bg-card)] text-[color:var(--color-text-primary)]'
            : 'text-[color:var(--color-text-secondary)]'
        }`}
      >
        Skip to upload
      </button>
    </div>
  );
}

function SkipPanel() {
  const navigate = useNavigate();
  return (
    <section className="mt-8 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-6 text-center">
      <h2 className="text-lg font-semibold">Ready to deliver another batch?</h2>
      <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">
        Your <code className="font-mono">metadata.csv</code> at the root, one{' '}
        <code className="font-mono">&lt;provider_id&gt;/&lt;subdir&gt;/</code> per title,
        case-sensitive filenames matching the CSV columns.
      </p>
      <button
        type="button"
        onClick={() => navigate('/upload')}
        className="mt-5 rounded bg-[color:var(--color-crimson)] px-6 py-3 text-sm font-medium text-white"
      >
        Start upload
      </button>
    </section>
  );
}

function Wizard({ onFinish }: { onFinish: () => void }) {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  function goNext() {
    if (step < 3) setStep(step + 1);
  }
  function goBack() {
    if (step > 1) setStep(step - 1);
  }
  function beginUpload() {
    onFinish();
    navigate('/upload');
  }

  return (
    <div className="mt-8">
      <StepIndicator current={step} total={3} />
      <section className="mt-4 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-6">
        {step === 1 && <RequirementsStep />}
        {step === 2 && <StructureStep />}
        {step === 3 && <BeginUploadStep />}
      </section>
      <div className="mt-4 flex items-center justify-between">
        <button
          type="button"
          onClick={goBack}
          disabled={step === 1}
          className="rounded border border-[color:var(--color-border)] px-4 py-2 text-sm disabled:opacity-30"
        >
          Back
        </button>
        {step < 3 ? (
          <button
            type="button"
            onClick={goNext}
            className="rounded bg-[color:var(--color-crimson)] px-4 py-2 text-sm font-medium text-white"
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            onClick={beginUpload}
            className="rounded bg-[color:var(--color-crimson)] px-4 py-2 text-sm font-medium text-white"
          >
            Begin upload
          </button>
        )}
      </div>
    </div>
  );
}

function StepIndicator({ current, total }: { current: number; total: number }) {
  const labels = ['Requirements', 'Upload structure', 'Begin upload'];
  return (
    <ol className="flex items-center gap-2 text-xs">
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => {
        const active = n === current;
        return (
          <li key={n} className="flex flex-1 items-center gap-2">
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full border ${
                active
                  ? 'border-[color:var(--color-crimson)] bg-[color:var(--color-crimson)] text-white'
                  : 'border-[color:var(--color-crimson)] bg-[color:var(--color-bg-primary)] text-[color:var(--color-text-primary)]'
              }`}
            >
              {n}
            </span>
            <span
              className={
                active
                  ? 'text-[color:var(--color-text-primary)]'
                  : 'text-[color:var(--color-text-secondary)]'
              }
            >
              {labels[n - 1]}
            </span>
            {n < total ? (
              <span className="ml-2 h-px flex-1 bg-[color:var(--color-border)]" />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function RequirementsStep() {
  return (
    <div>
      <h2 className="text-lg font-semibold">Requirements for media and metadata</h2>
      <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">
        Each delivery is a folder containing a single{' '}
        <code className="font-mono">metadata.csv</code> plus one subfolder per title with
        the title's video, audio, subtitles, and artwork.
      </p>

      <h3 className="mt-5 text-sm font-medium">Metadata</h3>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-[color:var(--color-text-secondary)]">
        <li>
          One CSV. Per row, set <code className="font-mono">content_type=movie</code> for
          films or <code className="font-mono">content_type=episode</code> for series.
          Mix freely in one delivery.
        </li>
        <li>
          <code className="font-mono">provider_id</code> is your unique ID for the title;
          it doubles as the title's subfolder name (letters, digits, _, -; max 64 chars).
        </li>
        <li>
          Filenames in the CSV must match uploaded files exactly (case-sensitive).
        </li>
      </ul>

      <h3 className="mt-5 text-sm font-medium">Media specs</h3>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-[color:var(--color-text-secondary)]">
        <li>Video: 16:9, min 1920×1080 (HD), recommended 3840×2160. ProRes 422 HQ, H.264, or HEVC in MOV/MP4.</li>
        <li>Poster: 2:3 portrait, min 1000×1500 (PNG preferred).</li>
        <li>Landscape and hero artwork: 16:9, min 1920×1080.</li>
        <li>Subtitles: SRT, VTT, or TTML, UTF-8.</li>
        <li>Per-language audio: WAV, 48 kHz / 16-bit min.</li>
      </ul>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <a
          href={TEMPLATE_URL}
          download="metadata.csv"
          className="rounded bg-[color:var(--color-crimson)] px-4 py-2 text-sm font-medium text-white"
        >
          Download metadata.csv
        </a>
        <a
          href={SAMPLE_URL}
          download
          className="rounded border border-[color:var(--color-border)] px-4 py-2 text-sm"
        >
          Download worked sample
        </a>
        <Link
          to="/spec"
          className="rounded border border-[color:var(--color-border)] px-4 py-2 text-sm"
        >
          Full spec & field defs
        </Link>
      </div>
    </div>
  );
}

function StructureStep() {
  return (
    <div>
      <h2 className="text-lg font-semibold">Upload structure</h2>
      <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">
        Make a single delivery folder. Put exactly one{' '}
        <code className="font-mono">metadata.csv</code> at its root, then one subfolder per
        title named by its <code className="font-mono">provider_id</code>. Inside each title
        folder, files go into <code className="font-mono">video/</code>,{' '}
        <code className="font-mono">audio/</code>,{' '}
        <code className="font-mono">subtitles/</code>, or{' '}
        <code className="font-mono">artwork/</code>. Filenames are yours to choose — the CSV
        columns tell us which file is which.
      </p>
      <pre className="mt-4 overflow-auto rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] p-4 font-mono text-xs leading-relaxed">
{`<your delivery folder>/
  metadata.csv               ← exactly one CSV; one row per title
  <provider_id>/             ← matches the row's provider_id
    video/<your filenames>   ← reference these names in
    audio/<your filenames>     video_filename, trailer_filename, etc.
    subtitles/<your files>
    artwork/<your files>
  <another_provider_id>/
    video/...
    artwork/...`}
      </pre>
      <p className="mt-3 text-sm text-[color:var(--color-text-secondary)]">
        On submit, DFX validates: every CSV row has a matching subfolder, every subfolder
        has a matching row, and every filename referenced in the CSV exists in the right
        subfolder.
      </p>
    </div>
  );
}

function BeginUploadStep() {
  return (
    <div className="text-center">
      <h2 className="text-lg font-semibold">Ready to upload</h2>
      <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">
        Use the Begin upload button below to open the upload page and your folder picker.
        Choose your delivery folder; we'll generate a unique batch ID and stream every
        file directly to storage.
      </p>
    </div>
  );
}
